import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ViewChild, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CpfCnpjPipe } from '../shared/pipes/cpf-cnpj.pipe';
import { MatSpinnerOverlayComponent } from '../components/mat-spinner-overlay/mat-spinner-overlay.component';
import { SnackBarService } from '../services/snack-bar-service.service';
import { CadastrarSocioComponent, CadastrarSocioDialogData } from './cadastrar-socio/cadastrar-socio.component';
import { ConfirmarSubmissaoComponent } from './confirmar-submissao/confirmar-submissao.component';
import { HabilitacaoSwapService } from './services/habilitacao-swap.service';
import {
  HabilitacaoSwapModel,
  HistoricoSwapModel,
  SocioModel,
  STATUS_LABELS,
  StatusHabilitacao,
} from './models';

@Component({
  selector: 'app-habilitacao-swap',
  templateUrl: './habilitacao-swap.component.html',
  styleUrls: ['./habilitacao-swap.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatTableModule,
    MatSpinnerOverlayComponent,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatBadgeModule,
    MatTabsModule,
    CpfCnpjPipe,
  ],
})
export class HabilitacaoSwapComponent implements AfterViewInit {
  // ==================== Dependencies ====================
  private readonly dialog = inject(MatDialog);
  private readonly service = inject(HabilitacaoSwapService);
  private readonly snack = inject(SnackBarService);

  // ==================== ViewChild ====================
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // ==================== State ====================
  public loading = false;
  public habilitacao: HabilitacaoSwapModel | null = null;
  public historico: HistoricoSwapModel[] = [];
  public selectedTabIndex = 0;

  // ==================== Mock Log Data ====================
  public logs: { dataHora: string; acao: string; usuario: string; detalhe: string }[] = [
    { dataHora: '25/02/2026 15:30', acao: 'ACESSO', usuario: 'admin.user', detalhe: 'Acessou a tela de habilitação SWAP.' },
    { dataHora: '25/02/2026 15:31', acao: 'INCLUSAO', usuario: 'admin.user', detalhe: 'Incluiu sócio João da Silva (CPF: 123.456.789-01).' },
    { dataHora: '25/02/2026 15:32', acao: 'INCLUSAO', usuario: 'admin.user', detalhe: 'Incluiu sócio Empresa ABC Ltda (CNPJ: AB.123.456/0001-99).' },
    { dataHora: '25/02/2026 15:35', acao: 'SUBMISSAO', usuario: 'admin.user', detalhe: 'Submeteu 2 sócio(s) para validação SWAP/Bacen.' },
    { dataHora: '22/02/2026 09:15', acao: 'STATUS', usuario: 'sistema', detalhe: 'Status atualizado para AJUSTE_SOLICITADO.' },
  ];

  // ==================== Data Source ====================
  readonly dataSource = new MatTableDataSource<SocioModel>([]);
  readonly displayedColumns: string[] = [
    'nome',
    'tipoDocumento',
    'cpfCnpj',
    'dataNascimento',
    'nomePai',
    'nomeMae',
    'acoes',
  ];

  readonly displayedColumnsReadOnly: string[] = [
    'nome',
    'tipoDocumento',
    'cpfCnpj',
    'dataNascimento',
    'nomePai',
    'nomeMae',
  ];

  // ==================== Getters ====================
  get status(): StatusHabilitacao {
    return this.habilitacao?.status || 'NAO_INICIADO';
  }

  get statusLabel(): string {
    return this.habilitacao?.statusLabel || STATUS_LABELS['NAO_INICIADO'];
  }

  get isReadOnly(): boolean {
    return this.habilitacao?.isReadOnly || false;
  }

  get isEditable(): boolean {
    return this.habilitacao?.isEditable ?? true;
  }

  get socios(): SocioModel[] {
    return this.habilitacao?.socios || [];
  }

  get columns(): string[] {
    return this.isReadOnly ? this.displayedColumnsReadOnly : this.displayedColumns;
  }

  get dataLiberacao(): string {
    return this.habilitacao?.dataLiberacaoFormatada || '';
  }

  get mensagemSwap(): string | null {
    return this.habilitacao?.mensagemSwap || null;
  }

  // ==================== Lifecycle ====================
  constructor() {
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  // ==================== Data Loading ====================
  private loadData(): void {
    this.loading = true;
    this.service.buscarHabilitacao().subscribe({
      next: (habilitacao) => {
        if (habilitacao) {
          this.habilitacao = habilitacao;
          this.dataSource.data = habilitacao.socios;
          this.historico = habilitacao.historico;
        } else {
          this.habilitacao = new HabilitacaoSwapModel(
            null, 'NAO_INICIADO', [], [], null, null, null
          );
          this.dataSource.data = [];
          this.historico = [];
        }
      },
      error: () => {
        this.loading = false;
        this.snack.flashMessage(
          'Erro ao carregar dados de habilitação',
          ['snackbar-error'], 'Fechar', 3000
        );
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  // ==================== Dialog Methods ====================
  cadastrarSocio(): void {
    const data: CadastrarSocioDialogData = {
      cpfsCnpjsExistentes: this.socios.map((s) => s.cpfCnpj.replace(/\D/g, '')),
    };
    const dialogRef = this.dialog.open(CadastrarSocioComponent, {
      width: '50%', data,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.sucesso) {
        this.loadData();
        this.snack.flashMessage('Sócio cadastrado com sucesso!', ['snackbar-success'], 'Fechar', 3000);
      }
    });
  }

  editarSocio(socio: SocioModel): void {
    const data: CadastrarSocioDialogData = {
      socio,
      cpfsCnpjsExistentes: this.socios.map((s) => s.cpfCnpj.replace(/\D/g, '')),
    };
    const dialogRef = this.dialog.open(CadastrarSocioComponent, {
      width: '50%', data,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.sucesso) {
        this.loadData();
        this.snack.flashMessage('Sócio atualizado com sucesso!', ['snackbar-success'], 'Fechar', 3000);
      }
    });
  }

  removerSocio(socio: SocioModel): void {
    if (!socio.id) return;
    if (!confirm(`Deseja realmente remover o sócio "${socio.nome}"?`)) return;

    this.loading = true;
    this.service.removerSocio(socio.id).subscribe({
      next: (response) => {
        if (response?.sucesso) {
          this.loadData();
          this.snack.flashMessage('Sócio removido com sucesso!', ['snackbar-success'], 'Fechar', 3000);
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snack.flashMessage('Erro ao remover sócio', ['snackbar-error'], 'Fechar', 3000);
      },
    });
  }

  // ==================== Submissão ====================
  submeterParaValidacao(): void {
    if (this.socios.length === 0) {
      this.snack.flashMessage('Cadastre pelo menos um sócio antes de submeter', ['snackbar-error'], 'Fechar', 3000);
      return;
    }
    const dialogRef = this.dialog.open(ConfirmarSubmissaoComponent, {
      width: '40%', data: { totalSocios: this.socios.length },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.sucesso) {
        this.loadData();
        this.snack.flashMessage('Dados submetidos para validação com sucesso!', ['snackbar-success'], 'Fechar', 5000);
      }
    });
  }

  // ==================== Consulta Status ====================
  consultarStatus(): void {
    this.loading = true;
    this.service.consultarStatusSwap().subscribe({
      next: (response) => {
        if (response?.sucesso) {
          this.loadData();
          this.snack.flashMessage('Status atualizado com sucesso!', ['snackbar-success'], 'Fechar', 3000);
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snack.flashMessage('Erro ao consultar status SWAP', ['snackbar-error'], 'Fechar', 3000);
      },
    });
  }

  // ==================== Utility Methods ====================
  scrollToTopAnimated(): void {
    const scrollToTop = () => {
      const currentScroll = document.documentElement.scrollTop || document.body.scrollTop;
      if (currentScroll > 0) {
        window.requestAnimationFrame(scrollToTop);
        window.scrollTo(0, 0);
      }
    };
    scrollToTop();
  }
}
